import React from "react";
import { Dialog, DialogActions, DialogContent, TextField } from "@mui/material";
import { Button, Label } from "reactstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { sendSurveyResult } from "services/_survey";
import { successToast, errorToast } from "components/toast";
import { captureChartAsBase64 } from "./surveyBody";
import logo from "../../../assets/images/Logo5.svg";

export const svgToPngBase64 = async (svgUrl, width = 444, height = 120) => {
  const svgText = await fetch(svgUrl).then((res) => {
    if (!res.ok) {
      throw new Error("Logo image failed");
    }
    return res.text();
  });

  const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
  const svgObjectUrl = URL.createObjectURL(svgBlob);

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      URL.revokeObjectURL(svgObjectUrl);
      reject(new Error("Logo image timed out"));
    }, 10000);

    const img = new Image();
    img.onload = () => {
      clearTimeout(timeout);
      const naturalWidth = img.naturalWidth || width;
      const naturalHeight = img.naturalHeight || height;
      const scale = Math.min(width / naturalWidth, height / naturalHeight);
      const drawWidth = Math.round(naturalWidth * scale);
      const drawHeight = Math.round(naturalHeight * scale);
      const canvas = document.createElement("canvas");
      canvas.width = drawWidth;
      canvas.height = drawHeight;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, drawWidth, drawHeight);
      ctx.drawImage(img, 0, 0, drawWidth, drawHeight);

      URL.revokeObjectURL(svgObjectUrl);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(svgObjectUrl);
      reject(new Error("Logo image failed"));
    };

    img.src = svgObjectUrl;
  });
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Enter a valid email address",
    )
    .required("Email is required field."),
});

const EmailDialog = ({
  open,
  settings,
  setOpen,
  beginAction,
  endAction,
  chart1Ref,
  chart2Ref,
  resultId,
  isMobileDevice,
}) => {
  const handleClose = () => {
    setOpen(false);
  };

  const handleEmailSubmit = async (values) => {
    if (!beginAction || !beginAction()) return;
    setOpen(false);

    try {
      let chartBase64Array = [];

      if (chart1Ref.current) {
        const chart1 = await captureChartAsBase64(chart1Ref, "symptoms");
        if (chart1) chartBase64Array.push(chart1);
      }

      if (chart2Ref.current) {
        const chart2 = await captureChartAsBase64(chart2Ref, "diagnostic");
        if (chart2) chartBase64Array.push(chart2);
      }
      const logoBase64 = await svgToPngBase64(logo);

      const payload = {
        id: resultId,
        chartData: chartBase64Array,
        email: values.email,
        isMobile: isMobileDevice(),
        logo: logoBase64,
      };

      const data = await sendSurveyResult(payload);
      if (data.isSuccess) {
        successToast(data.message);
      } else {
        errorToast(data.message || "Failed to send email");
      }
    } catch (err) {
      errorToast("Failed to send email");
    } finally {
      endAction?.();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <Label className="p-[20px] px-[34px] pb-0 text-[14px] ">
        Enter Email Address <span className="text-[#e74c3c]">*</span>
      </Label>

      <Formik
        initialValues={{ email: "" }}
        validationSchema={validationSchema}
        onSubmit={handleEmailSubmit}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form id="subscription-form" className="email_subscription">
            <DialogContent className="py-0">
              <Field
                as={TextField}
                autoFocus
                required
                margin="dense"
                id="email"
                name="email"
                label="Email"
                type="text"
                fullWidth
                variant="standard"
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
            </DialogContent>

            <DialogActions className="mt-[15px] px-[15px] ">
              <Button
                className="cancel_btn"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                form="subscription-form"
                className="submit_btn"
                disabled={isSubmitting}
              >
                {settings?.survey_email_button_label || "Send Email"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EmailDialog;
